import Image from "next/image";
import { useIntl } from "react-intl";
import Link from "next/link";
import {
  addBasePathToStrapiMedia,
  formatStrapiEntity,
  StrapiEntity,
  StrapiImage,
} from "@/utils/strapi";
import { CtaButton } from "@/components/common/ctaButton/CtaButton";
import { injectPurifiedHTML } from "@/utils/injectPurifiedHTML";
import { removeEmptyRel } from "@/utils/removeEmptyRel";
import styles from "./AgencyCard.module.scss";

export type AgencyCardProps = {
  image: StrapiEntity<StrapiImage>;
  label: string;
  name: string;
  address: string;
  phone?: string;
  email?: string;
  url?: string;
  openInNewTab?: boolean;
};

export const AgencyCard = ({
  image,
  label,
  name,
  address,
  phone,
  email,
  url,
  openInNewTab,
}: AgencyCardProps) => {
  const strapiImage = formatStrapiEntity(image);
  const intl = useIntl();

  const onClickCta = () => {
    if (url !== undefined) {
      if (openInNewTab) {
        window.open(url, "_blank");
      } else {
        window.location.href = url;
      }
    }
  };

  return (
    <div className={styles.agencyCard}>
      <div className={styles.imageContainer}>
        <div className={styles.image}>
          <Image
            src={addBasePathToStrapiMedia(strapiImage)}
            objectFit="contain"
            objectPosition="center"
            alt={strapiImage.alternativeText}
            layout="fill"
          />
        </div>
      </div>
      <div className={styles.infosContainer}>
        <div className={styles.label}>{label}</div>
        <div className={styles.name}>{name}</div>
        <div
          className={styles.address}
          dangerouslySetInnerHTML={injectPurifiedHTML(removeEmptyRel(address))}
        />
        {phone !== undefined && (
          <div className={styles.phone}>
            <Link href={`tel:${phone}`}>
              <a>{phone}</a>
            </Link>
          </div>
        )}
        {email !== undefined && (
          <div className={styles.email}>
            <Link href={`mailto:${email}`}>
              <a>{intl.formatMessage({ id: "contactByMail" })}</a>
            </Link>
          </div>
        )}
        {url !== undefined && (
          <CtaButton
            isFullWidth
            label={intl.formatMessage({ id: "bookCategory" })}
            onClick={() => onClickCta()}
          />
        )}
      </div>
    </div>
  );
};
